import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import "./Gaming.css";

const HERO_SLIDES = [
  {
    image: "/images/vr-bg1.jpg",
    tag: "Immersive Entertainment",
    title: "Beyond Gaming. Into the Experience.",
    description:
      "Discover VR adventures, competitive racing, PS5 gaming, interactive play and snooker at The HexaPlay.",
  },
  {
    image: "/images/vr-bg.jpg",
    tag: "Your Play. Your Rules.",
    title: "Five Ways to Experience the Thrill.",
    description:
      "Bring your friends, challenge your skills and turn an ordinary outing into an extraordinary experience.",
  },
];

const EXPERIENCES = [
  {
    id: "vr-gaming",
    number: "01",
    category: "IMMERSIVE GAMING",
    title: "VR Gaming",
    tagline: "Step Into a World Beyond Reality",
    description:
      "Step into the future of entertainment with immersive virtual reality. Put on a VR headset, explore virtual worlds, take on thrilling challenges and team up with friends for unforgettable adventures.",
    image:
      "https://images.unsplash.com/photo-1593508512255-86ab42a8e620?w=1000&h=750&fit=crop",
    imageAlt: "Immersive virtual reality gaming experience",
    icon: "🥽",
    button: "Book Now",
    highlights: [
      "Propagation VR — horror-survival gameplay",
      "Epic Roller Coasters — virtual roller-coaster adventures",
      "VR Cricket — virtual cricket challenges",
      "Multiplayer VR — shared team challenges",
    ],
    note:
      "Game titles and multiplayer options should be confirmed against the VR games installed at the venue.",
    featured: true,
  },
  {
    id: "car-simulator",
    number: "02",
    category: "RACING EXPERIENCE",
    title: "Car Simulator",
    tagline: "Two Drivers. One Race. All the Excitement.",
    description:
      "Take the driver's seat and challenge your friend to a thrilling racing experience. Grip the steering wheel, control the pedals and test your driving skills through competitive circuits and time trials.",
    image:
      "https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?w=1000&h=750&fit=crop",
    imageAlt: "Performance car for a racing simulator experience",
    icon: "🏎️",
    button: "Book Your Race",
    highlights: [
      "Head-to-Head Racing — challenge another player",
      "Time Trial — aim for your fastest lap",
      "Circuit Racing — master corners and racing lines",
      "Competitive Challenges — put your driving skills to the test",
    ],
    note:
      "Two-player racing requires two connected simulator stations and a compatible multiplayer game.",
    featured: false,
  },
  {
    id: "ps5-gaming",
    number: "03",
    category: "CONSOLE GAMING",
    title: "PS5 Semi-Private Station",
    tagline: "Your Game. Your Space. Your Challenge.",
    description:
      "Enjoy console gaming in a comfortable semi-private station. Play on a big screen, challenge friends, team up for multiplayer matches and choose an experience that matches your style.",
    image:
      "https://images.unsplash.com/photo-1606144042614-b2417e99c4e3?w=1000&h=750&fit=crop",
    imageAlt: "Console gaming equipment and controller",
    icon: "🎮",
    button: "Book Now",
    highlights: [
      "Football — competitive sports matches",
      "Wrestling — action-packed matchups",
      "Fighting Games — test your timing and strategy",
      "Racing & Adventures — explore different game genres",
    ],
    note:
      "Confirm installed titles and editions before publishing specific game names.",
    featured: false,
  },
  {
    id: "nex-playground",
    number: "04",
    category: "ACTIVE FAMILY FUN",
    title: "NEX Playground",
    tagline: "Move, Play & Have Fun!",
    description:
      "Bring movement and interactive entertainment together with motion-based gaming. Jump, dodge, dance and play through experiences designed to make screen time more active and engaging for kids and families.",
    image:
      "https://images.unsplash.com/photo-1596461404969-9ae70f2830c1?w=1000&h=750&fit=crop",
    imageAlt: "Family-friendly play and entertainment",
    icon: "🕹️",
    button: "Explore NEX Playground",
    highlights: [
      "Fruit Ninja — slice virtual fruit with movement",
      "Whac-A-Mole — enjoy quick-reaction challenges",
      "Go Keeper — test your virtual goalkeeping skills",
      "Party Fowl — bring playful party challenges to life",
    ],
    note:
      "Verify the games available on the venue's NEX Playground device and subscription.",
    featured: false,
  },
  {
    id: "snooker",
    number: "05",
    category: "SKILL & STRATEGY",
    title: "Snooker",
    tagline: "Aim. Strike. Pot. Repeat.",
    description:
      "Enjoy a relaxed yet competitive snooker session. Line up your shots, plan your next move and challenge your friends to a friendly match. Sharpen your accuracy, cue control and strategic thinking with every frame.",
    image:
      "https://images.unsplash.com/photo-1609710228159-0fa9bd7c771b?w=1000&h=750&fit=crop",
    imageAlt: "Indoor cue sports and recreational gaming",
    icon: "🎱",
    button: "Play Snooker",
    highlights: [
      "Friendly Matches — play one-on-one or in a group",
      "Precision Practice — improve your aim and cue control",
      "Skill Development — build consistency with every shot",
      "Strategy & Focus — plan your next move",
    ],
    note:
      "Ensure the description reflects the actual snooker table and facilities available at the venue.",
    featured: false,
  },
];

const GALLERY_ITEMS = [
  {
    id: "gallery-vr-1",
    type: "vr",
    media: "image",
    src: "https://images.unsplash.com/photo-1593508512255-86ab42a8e620?w=800&h=650&fit=crop",
    alt: "Virtual reality gaming",
    title: "Virtual Worlds",
  },
  {
    id: "gallery-vr-2",
    type: "vr",
    media: "image",
    src: "https://images.unsplash.com/photo-1617802690992-15d93263d3a9?w=800&h=650&fit=crop",
    alt: "VR headset and immersive gameplay",
    title: "VR Adventures",
  },
  {
    id: "gallery-car-1",
    type: "car",
    media: "image",
    src: "https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?w=800&h=650&fit=crop",
    alt: "Performance racing car",
    title: "Racing Thrills",
  },
  {
    id: "gallery-car-2",
    type: "car",
    media: "image",
    src: "https://images.unsplash.com/photo-1503376780353-7e6692767b70?w=800&h=650&fit=crop",
    alt: "Sports car driving experience",
    title: "Drive to Win",
  },
  {
    id: "gallery-ps5-1",
    type: "ps5",
    media: "image",
    src: "https://images.unsplash.com/photo-1606144042614-b2417e99c4e3?w=800&h=650&fit=crop",
    alt: "Console and controller gaming",
    title: "Console Gaming",
  },
  {
    id: "gallery-ps5-2",
    type: "ps5",
    media: "image",
    src: "https://images.unsplash.com/photo-1542751371-adc38448a05e?w=800&h=650&fit=crop",
    alt: "Gaming and esports setup",
    title: "Challenge Your Friends",
  },
  {
    id: "gallery-nex-1",
    type: "nex",
    media: "image",
    src: "https://images.unsplash.com/photo-1596461404969-9ae70f2830c1?w=800&h=650&fit=crop",
    alt: "Family-friendly recreational play",
    title: "Active Play",
  },
  {
    id: "gallery-snooker-1",
    type: "snooker",
    media: "image",
    src: "https://images.unsplash.com/photo-1609710228159-0fa9bd7c771b?w=800&h=650&fit=crop",
    alt: "Cue sports and recreational play",
    title: "Precision & Strategy",
  },
];

const GALLERY_FILTERS = [
  { key: "all", label: "All Experiences", icon: "✦" },
  { key: "vr", label: "VR Gaming", icon: "🥽" },
  { key: "car", label: "Car Simulator", icon: "🏎️" },
  { key: "ps5", label: "PS5", icon: "🎮" },
  { key: "nex", label: "NEX Playground", icon: "🕹️" },
  { key: "snooker", label: "Snooker", icon: "🎱" },
];

const ArrowIcon = ({ direction = "right" }) => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    {direction === "left" ? (
      <path d="m15 18-6-6 6-6" />
    ) : (
      <>
        <path d="M5 12h14" />
        <path d="m12 5 7 7-7 7" />
      </>
    )}
  </svg>
);

const Gaming = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [filter, setFilter] = useState("all");
  const [failedImages, setFailedImages] = useState({});
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    if (isPaused) return undefined;

    const timer = window.setInterval(() => {
      setCurrentSlide((previous) => (previous + 1) % HERO_SLIDES.length);
    }, 5000);

    return () => window.clearInterval(timer);
  }, [isPaused]);

  const changeSlide = (direction) => {
    setCurrentSlide((previous) => {
      if (direction === "next") {
        return (previous + 1) % HERO_SLIDES.length;
      }

      return (previous - 1 + HERO_SLIDES.length) % HERO_SLIDES.length;
    });
  };

  const visibleGallery =
    filter === "all"
      ? GALLERY_ITEMS
      : GALLERY_ITEMS.filter((item) => item.type === filter);

  const markImageFailed = (id) => {
    setFailedImages((previous) => ({ ...previous, [id]: true }));
  };

  return (
    <main className="gaming-page">
      {/* HERO */}
      <section
        className="gaming-hero"
        aria-label="The HexaPlay gaming experiences"
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
        onFocus={() => setIsPaused(true)}
        onBlur={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget)) {
            setIsPaused(false);
          }
        }}
      >
        {HERO_SLIDES.map((slide, index) => (
          <div
            className={`gaming-hero-slide ${
              index === currentSlide ? "is-active" : ""
            }`}
            key={slide.image}
            aria-hidden={index !== currentSlide}
          >
            <img
              className="gaming-hero-image"
              src={slide.image}
              alt=""
              fetchPriority={index === 0 ? "high" : "auto"}
            />

            <div className="gaming-hero-shade" />

            <div className="gaming-hero-content" key={currentSlide}>
              <span className="gaming-eyebrow">
                <span className="gaming-eyebrow-dot" />
                {slide.tag}
              </span>

              <h1>{slide.title}</h1>
              <p>{slide.description}</p>

              <div className="gaming-hero-actions">
                <Link to="/VRGamingg" className="gaming-button gaming-button-primary">
                  Explore Gaming <ArrowIcon />
                </Link>

                <a
                  href="#gaming-experiences"
                  className="gaming-button gaming-button-outline"
                >
                  Discover Experiences
                </a>
              </div>

              <div className="gaming-hero-stat">
                <span className="gaming-stat-number">05</span>
                <span className="gaming-stat-label">
                  Ways to play
                  <br />
                  Endless possibilities
                </span>
              </div>
            </div>
          </div>
        ))}

        <div className="gaming-hero-controls">
          <button
            type="button"
            className="gaming-arrow"
            aria-label="Previous hero slide"
            onClick={() => changeSlide("previous")}
          >
            <ArrowIcon direction="left" />
          </button>

          <div className="gaming-slide-count" aria-live="polite">
            <span>{String(currentSlide + 1).padStart(2, "0")}</span>
            <span className="gaming-count-divider" />
            <span>{String(HERO_SLIDES.length).padStart(2, "0")}</span>
          </div>

          <button
            type="button"
            className="gaming-arrow"
            aria-label="Next hero slide"
            onClick={() => changeSlide("next")}
          >
            <ArrowIcon />
          </button>
        </div>

        <div className="gaming-hero-progress" aria-hidden="true">
          <span
            key={currentSlide}
            style={{ animationPlayState: isPaused ? "paused" : "running" }}
          />
        </div>
      </section>

      {/* INTRODUCTION */}
      <section className="gaming-intro">
        <div className="gaming-intro-inner">
          <div className="gaming-intro-heading">
            <span className="gaming-section-label">THE HEXAPLAY EXPERIENCE</span>
            <h2>
              Find Your Game.
              <br />
              <span>Own the Moment.</span>
            </h2>
          </div>

          <div className="gaming-intro-copy">
            <p>
              Every player has a different kind of thrill. From stepping into
              virtual worlds and racing against friends to enjoying console
              battles, active family play and strategic snooker, discover an
              experience that fits your mood.
            </p>

            <a href="#gaming-experiences" className="gaming-text-link">
              Explore all experiences <ArrowIcon />
            </a>
          </div>
        </div>

        <div className="gaming-category-strip" aria-label="Gaming categories">
          {EXPERIENCES.map((experience) => (
            <a
              key={experience.id}
              href={`#${experience.id}`}
              className="gaming-category-item"
            >
              <span className="gaming-category-icon">{experience.icon}</span>
              <span>{experience.title}</span>
              <span className="gaming-category-arrow">
                <ArrowIcon />
              </span>
            </a>
          ))}
        </div>
      </section>

      {/* FIVE GAMING EXPERIENCES */}
      <section className="gaming-experiences" id="gaming-experiences">
        <div className="gaming-section-heading">
          <span className="gaming-section-label">PLAY YOUR WAY</span>
          <h2>Explore Our Gaming Zones</h2>
          <p>
            Discover your next favourite experience, whether you love immersive
            adventures, friendly competition or relaxed recreation.
          </p>
        </div>

        <div className="gaming-experience-list">
          {EXPERIENCES.map((experience, index) => (
            <article
              className={`gaming-experience-card ${
                experience.featured ? "gaming-experience-featured" : ""
              }`}
              id={experience.id}
              key={experience.id}
            >
              <div className="gaming-experience-visual">
                {failedImages[experience.id] ? (
                  <div className="gaming-image-fallback">
                    <span>{experience.icon}</span>
                    <span>{experience.title}</span>
                  </div>
                ) : (
                  <img
                    src={experience.image}
                    alt={experience.imageAlt}
                    loading="lazy"
                    onError={() => markImageFailed(experience.id)}
                  />
                )}

                <span className="gaming-experience-number">
                  {experience.number}
                </span>

                <span className="gaming-experience-icon">
                  {experience.icon}
                </span>

                <span className="gaming-image-caption">
                  {experience.category}
                </span>
              </div>

              <div className="gaming-experience-details">
                <span className="gaming-card-kicker">
                  EXPERIENCE {experience.number}
                </span>

                <h3>{experience.title}</h3>
                <h4>{experience.tagline}</h4>
                <p className="gaming-experience-description">
                  {experience.description}
                </p>

                <div className="gaming-highlights">
                  <h5>What to experience</h5>
                  <ul>
                    {experience.highlights.map((highlight) => (
                      <li key={highlight}>
                        <span className="gaming-check" aria-hidden="true">
                          ↗
                        </span>
                        <span>{highlight}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <Link
                  to="/VRGamingg"
                  className="gaming-button gaming-button-dark"
                  aria-label={`${experience.button} — ${experience.title}`}
                >
                  {experience.button} <ArrowIcon />
                </Link>

                <p className="gaming-availability-note">
                  <span aria-hidden="true">ⓘ</span> {experience.note}
                </p>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* GAMING GALLERY */}
      <section className="gaming-gallery" id="gaming-gallery">
        <div className="gaming-gallery-inner">
          <div className="gaming-section-heading gaming-gallery-heading">
            <span className="gaming-section-label">A CLOSER LOOK</span>
            <h2>Gaming Gallery</h2>
            <p>
              A glimpse into the worlds of play, competition and entertainment
              at The HexaPlay.
            </p>
          </div>

          <div className="gaming-gallery-filters" aria-label="Filter gallery">
            {GALLERY_FILTERS.map((item) => (
              <button
                type="button"
                key={item.key}
                className={`gaming-filter ${
                  filter === item.key ? "is-active" : ""
                }`}
                onClick={() => setFilter(item.key)}
                aria-pressed={filter === item.key}
              >
                <span aria-hidden="true">{item.icon}</span>
                {item.label}
              </button>
            ))}
          </div>

          <div className="gaming-gallery-grid" aria-live="polite">
            {visibleGallery.map((item, index) => (
              <article
                className={`gaming-gallery-item ${
                  index === 0 ? "gaming-gallery-item-featured" : ""
                }`}
                key={item.id}
              >
                <div className="gaming-gallery-media">
                  {failedImages[item.id] ? (
                    <div className="gaming-image-fallback">
                      <span>🎮</span>
                      <span>{item.title}</span>
                    </div>
                  ) : item.media === "video" ? (
                    <video
                      src={item.src}
                      controls
                      preload="metadata"
                      onError={() => markImageFailed(item.id)}
                    />
                  ) : (
                    <img
                      src={item.src}
                      alt={item.alt}
                      loading="lazy"
                      onError={() => markImageFailed(item.id)}
                    />
                  )}

                  <div className="gaming-gallery-overlay">
                    <span className="gaming-gallery-type">
                      {GALLERY_FILTERS.find((entry) => entry.key === item.type)
                        ?.label || item.type}
                    </span>
                    <h3>{item.title}</h3>
                  </div>
                </div>
              </article>
            ))}
          </div>

          <p className="gaming-gallery-note">
            Illustrative images are used as visual references. Replace them
            with actual HexaPlay venue photographs for an authentic gallery.
          </p>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="gaming-final-cta">
        <div className="gaming-cta-orb gaming-cta-orb-one" />
        <div className="gaming-cta-orb gaming-cta-orb-two" />

        <div className="gaming-final-cta-content">
          <span className="gaming-section-label">YOUR NEXT ADVENTURE STARTS HERE</span>
          <h2>Ready, Player One?</h2>
          <p>
            Gather your people, choose your experience and make your next
            gaming session one to remember.
          </p>

          <Link to="/VRGamingg" className="gaming-button gaming-button-primary">
            Book Your Experience <ArrowIcon />
          </Link>
        </div>
      </section>
    </main>
  );
};

export default Gaming;